import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Eye, FileText, Image as ImageIcon, File as FileIcon, FileSpreadsheet } from "lucide-react";
import DocumentPreviewModal from "./DocumentPreviewModal";
import { api, API } from "../../lib/api";
import { toast } from "sonner";

// Logo NON esposto come filtro nel portale cliente (per brief).
const FILTER_TYPES = ["Contratto", "Altro"];

function fmtSize(b) {
    if (b == null) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
function fileIcon(ct) {
    const t = (ct || "").toLowerCase();
    if (t.startsWith("image/")) return <ImageIcon size={20} strokeWidth={1.75} />;
    if (t.includes("pdf")) return <FileText size={20} strokeWidth={1.75} />;
    if (t.includes("spreadsheet") || t.includes("excel") || t.includes("csv")) return <FileSpreadsheet size={20} strokeWidth={1.75} />;
    if (t.startsWith("text/") || t.includes("word") || t.includes("document")) return <FileText size={20} strokeWidth={1.75} />;
    return <FileIcon size={20} strokeWidth={1.75} />;
}
function TypeChip({ type }) {
    const cls = type === "Contratto" ? "lt-chip-doc--contratto"
        : type === "Logo" ? "lt-chip-doc--logo"
        : "lt-chip-doc--altro";
    return <span className={`lt-chip-doc ${cls}`} data-testid={`portal-doc-type-${type}`}>{type}</span>;
}
function CategoryBadge({ name }) {
    if (!name) return null;
    return (
        <span
            style={{
                display: "inline-flex", alignItems: "center", padding: "2px 8px",
                borderRadius: 3, fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase",
                background: "rgba(229,255,0,0.10)", color: "#E5FF00",
                border: "1px solid rgba(229,255,0,0.35)", marginLeft: 8,
                fontFamily: 'IBM Plex Mono, ui-monospace, monospace', fontWeight: 500,
            }}
            data-testid="portal-doc-category-badge"
        >
            {name}
        </span>
    );
}

async function streamDownload(id, fallbackName) {
    try {
        const res = await fetch(`${API}/client/documenti/${id}/download`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const cd = res.headers.get("Content-Disposition") || "";
        let filename = fallbackName || "documento";
        const m1 = cd.match(/filename\*=UTF-8''([^;]+)/i);
        const m2 = cd.match(/filename="([^"]+)"/i);
        if (m1) filename = decodeURIComponent(m1[1]);
        else if (m2) filename = m2[1];
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
        toast.error("Download fallito");
    }
}

export default function PortalDocumenti() {
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState(null);
    const [categories, setCategories] = useState([]);
    const [typeFilter, setTypeFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [previewItem, setPreviewItem] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadCounters = useCallback(async () => {
        try {
            const { data } = await api.get("/client/documenti/counters");
            setCounters(data);
        } catch { /* ignore */ }
    }, []);

    const loadCategories = useCallback(async () => {
        try {
            const { data } = await api.get("/client/document-categories");
            setCategories(data.items || []);
        } catch { /* ignore — endpoint client potrebbe mancare in versioni vecchie */ }
    }, []);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, page_size: 100 };
            if (typeFilter) params.type = typeFilter;
            if (categoryFilter) params.category_id = categoryFilter;
            const { data } = await api.get("/client/documenti", { params });
            setItems(data.items || []);
        } catch {
            toast.error("Errore caricamento documenti");
        } finally { setLoading(false); }
    }, [typeFilter, categoryFilter]);

    useEffect(() => { loadCounters(); }, [loadCounters]);
    useEffect(() => { loadCategories(); }, [loadCategories]);
    useEffect(() => { loadList(); }, [loadList]);

    const counterCards = useMemo(() => {
        const c = counters || { totali: 0, per_tipo: {} };
        const contratti = c.per_tipo?.Contratto ?? 0;
        const logo = c.per_tipo?.Logo ?? 0;
        const altri = (c.totali ?? 0) - contratti - logo;
        return [
            { key: "totali",    label: "Totali",    value: c.totali ?? 0 },
            { key: "contratti", label: "Contratti", value: contratti },
            { key: "altri",     label: "Altri",     value: Math.max(altri, 0) },
        ];
    }, [counters]);

    return (
        <div style={{ padding: "32px 40px", maxWidth: 1100 }} data-testid="portal-documenti-page">
            <h1 className="lt-h1" style={{ marginBottom: 8 }}>Documenti</h1>
            <p className="lt-sub" style={{ marginBottom: 24 }}>Documenti condivisi con te da ENERLEFT.</p>

            <div className="grid" style={{ gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 20 }} data-testid="portal-doc-counters">
                {counterCards.map((c) => (
                    <div key={c.key} className="lt-counter-card" style={{ cursor: "default" }} data-testid={`portal-doc-counter-${c.key}`}>
                        <div className="lt-counter-card__value">{c.value}</div>
                        <div className="lt-counter-card__label">{c.label}</div>
                    </div>
                ))}
            </div>

            <div className="lt-segmented" style={{ marginBottom: 12 }} data-testid="portal-doc-filter-type">
                <button type="button" className={"lt-segmented__btn" + (typeFilter === "" ? " is-active" : "")} onClick={() => setTypeFilter("")}>Tutti</button>
                {FILTER_TYPES.map((t) => (
                    <button key={t} type="button" className={"lt-segmented__btn" + (typeFilter === t ? " is-active" : "")} onClick={() => setTypeFilter(t)} data-testid={`portal-doc-filter-${t}`}>{t}</button>
                ))}
            </div>

            {categories.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }} data-testid="portal-doc-filter-categories">
                    <button
                        type="button"
                        className={"lt-chip-filter" + (categoryFilter === "" ? " is-active" : "")}
                        onClick={() => setCategoryFilter("")}
                        data-testid="portal-doc-filter-cat-all"
                        style={chipStyle(categoryFilter === "")}
                    >
                        Tutte le categorie
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            className={"lt-chip-filter" + (categoryFilter === c.id ? " is-active" : "")}
                            onClick={() => setCategoryFilter(c.id)}
                            data-testid={`portal-doc-filter-cat-${c.id}`}
                            style={chipStyle(categoryFilter === c.id)}
                        >
                            {c.name}
                        </button>
                    ))}
                </div>
            )}

            {loading ? (
                <div style={{ color: "#8A8F88" }}>Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="lt-card" style={{ padding: 40, textAlign: "center" }} data-testid="portal-doc-empty">
                    <p className="lt-sub">Nessun documento condiviso al momento.</p>
                </div>
            ) : (
                <div className="lt-doc-card-list" data-testid="portal-doc-list">
                    {items.map((d) => (
                        <div key={d.id} className="lt-doc-card" data-testid={`portal-doc-row-${d.id}`}>
                            <div className="lt-doc-card__icon" aria-hidden="true">{fileIcon(d.content_type)}</div>
                            <div className="lt-doc-card__main">
                                <div className="lt-doc-card__title">
                                    <span className="lt-doc-card__name">{d.name}</span>
                                    <TypeChip type={d.type} />
                                    {d.category?.name && <CategoryBadge name={d.category.name} />}
                                </div>
                                <div className="lt-doc-card__meta">
                                    {d.original_filename} · {fmtSize(d.size)} · {fmtDate(d.created_at)}
                                </div>
                                {d.description && <div className="lt-doc-card__notes" style={{ color: "#5F6560" }}>{d.description}</div>}
                                {d.notes && <div className="lt-doc-card__notes">{d.notes}</div>}
                            </div>
                            <div className="lt-doc-card__action" style={{ display: "flex", gap: 8 }}>
                                <button
                                    className="lt-btn-secondary"
                                    onClick={() => setPreviewItem(d)}
                                    data-testid={`portal-doc-preview-${d.id}`}
                                    title="Visualizza"
                                    aria-label="Visualizza documento"
                                >
                                    <Eye size={16} strokeWidth={2} />
                                    <span className="lt-btn-label-mobile-hide">Visualizza</span>
                                </button>
                                <button
                                    className="lt-btn-secondary"
                                    onClick={() => streamDownload(d.id, d.original_filename)}
                                    data-testid={`portal-doc-download-${d.id}`}
                                    title="Scarica"
                                    aria-label="Scarica documento"
                                >
                                    <Download size={16} strokeWidth={2} />
                                    <span className="lt-btn-label-mobile-hide">Scarica</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {previewItem && (
                <DocumentPreviewModal
                    item={previewItem}
                    downloadUrl={`${API}/client/documenti/${previewItem.id}/download`}
                    onDownload={() => streamDownload(previewItem.id, previewItem.original_filename)}
                    onClose={() => setPreviewItem(null)}
                />
            )}
        </div>
    );
}

function chipStyle(active) {
    return {
        padding: "6px 12px",
        borderRadius: 3,
        fontSize: 12,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        border: active ? "1px solid #E5FF00" : "1px solid rgba(255,255,255,0.15)",
        background: active ? "rgba(229,255,0,0.10)" : "transparent",
        color: active ? "#E5FF00" : "#8A8F88",
        cursor: "pointer",
        fontFamily: "IBM Plex Mono, ui-monospace, monospace",
        fontWeight: 500,
        transition: "all 120ms ease",
    };
}
